import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { IAssistantConfig } from '@/features/assistant/types';
import { transformConfigurationPricing } from '@/features/assistant/utils';
import { ConfigurationDrawerProvider } from '@/features/assistant/context';
import { useGetAccountProductConfig } from '../../../hooks';
import { IQuoteLineItem } from '../../../types';
import { EditableWindowSection } from './EditableWindowSection';

interface QuoteImage { fileName: string; imageUrl: string; }

interface Props {
  quoteId: string;
  accountId?: string;
  lineItems: IQuoteLineItem[];
  vatPercentage: number;
  customDiscountPercentage: number;
  customDiscountAmount?: number;
  discountType?: 'percentage' | 'amount';
  vssVatPercentage: number;
  marginPercentage?: number;
  quoteImages?: QuoteImage[];
  companyAccount?: { dogma_deliverycharge?: boolean; [key: string]: any };
}

const EMPTY_CONFIG: IAssistantConfig = {
  version: '1.0.0', configName: '', lastUpdated: '', brandName: '',
  productLine: '', chatbotConfig: { greeting: '', language: 'en-GB', currency: 'GBP', units: 'metric', defaultRegion: 'UK' },
  questionFlow: [], validationRules: [], pricingRules: [],
};

export const QuoteWindowAmend: React.FC<Props> = ({
  quoteId, accountId, lineItems, vatPercentage, customDiscountPercentage,
  customDiscountAmount, discountType, vssVatPercentage, marginPercentage = 0,
  quoteImages = [], companyAccount,
}) => {
  const { data: accountConfig, isLoading } = useGetAccountProductConfig(accountId);

  const assistantConfig = useMemo<IAssistantConfig>(() => {
    if (!accountConfig) return EMPTY_CONFIG;
    return transformConfigurationPricing(accountConfig, marginPercentage, accountConfig.discountConfig, accountConfig.pricingRuleDiscounts) as IAssistantConfig;
  }, [accountConfig, marginPercentage]);

  // Always render ConfigurationDrawerProvider so EditableWindowSection is always inside it
  return (
    <ConfigurationDrawerProvider
      config={assistantConfig}
      questionFlow={assistantConfig.questionFlow ?? []}
      marginPercentage={marginPercentage}
    >
      {isLoading ? (
        <div className="bg-white rounded-lg p-6 flex justify-center"><Spin /></div>
      ) : (
        <EditableWindowSection
          quoteId={quoteId}
          lineItems={lineItems}
          vatPercentage={vatPercentage}
          customDiscountPercentage={customDiscountPercentage}
          customDiscountAmount={customDiscountAmount}
          discountType={discountType}
          vssVatPercentage={vssVatPercentage}
          quoteImages={quoteImages}
          companyAccount={companyAccount}
        />
      )}
    </ConfigurationDrawerProvider>
  );
};
